import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, ArrowRight, AlertTriangle, Loader2, Bot, Users, Radio } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from '../theme/ThemeToggle';

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={s.root} className="login-root">
      {/* Panel de marca (se oculta en pantallas angostas) */}
      <aside style={s.brandPanel} className="login-brand">
        <div style={s.brandTop}>
          <div style={s.logoBox}>EAS</div>
          <div>
            <div style={s.brandName}>Portal de Atención</div>
            <div style={s.brandSub}>Con SARA, asistente de IA</div>
          </div>
        </div>
        <div>
          <h2 style={s.pitch}>La IA atiende. Tu equipo cierra.</h2>
          <ul style={s.features}>
            <li style={s.feature}><Bot size={18} /> SARA responde y escala los casos que necesitan a una persona</li>
            <li style={s.feature}><Users size={18} /> Los casos se reparten solos entre los asesores disponibles</li>
            <li style={s.feature}><Radio size={18} /> Supervisión en vivo de la operación</li>
          </ul>
        </div>
        <div style={s.brandFoot}>Escuela de Audio y Sonido · Envigado, Colombia</div>
      </aside>

      {/* Formulario */}
      <main style={s.formPanel}>
        <div style={s.toggleWrap}><ThemeToggle style={s.toggle} /></div>
        <div style={s.card}>
          <div style={s.mobileLogo} className="login-mobile-logo">
            <div style={{ ...s.logoBox, width: 44, height: 44 }}>EAS</div>
          </div>
          <h1 style={s.title}>Bienvenido</h1>
          <p style={s.subtitle}>Ingresa con tu correo y tu contraseña.</p>

          <form onSubmit={handleSubmit} style={s.form}>
            <label style={s.field}>
              <span style={s.label}>Correo</span>
              <span style={s.inputWrap}>
                <Mail size={16} style={s.inputIcon} />
                <input style={s.input} type="text" value={username} onChange={e => setUsername(e.target.value)}
                  placeholder="tu@correo.com" autoComplete="username" required autoFocus />
              </span>
            </label>

            <label style={s.field}>
              <span style={s.label}>Contraseña</span>
              <span style={s.inputWrap}>
                <Lock size={16} style={s.inputIcon} />
                <input style={s.input} type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" required />
                <button type="button" style={s.eyeBtn} onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </span>
            </label>

            {error && (
              <div style={s.errorBox} role="alert"><AlertTriangle size={16} style={{ flexShrink: 0 }} /> {error}</div>
            )}

            <button type="submit" className="ui-primary" style={s.btn} disabled={loading}>
              {loading ? <><Loader2 size={17} className="ui-spin" /> Verificando…</> : <>Ingresar <ArrowRight size={17} /></>}
            </button>
          </form>
          <p style={s.help}>¿Olvidaste tu contraseña? Pídele al administrador que la restablezca.</p>
        </div>
      </main>

      <style>{`
        @media (max-width: 860px) {
          .login-brand { display: none !important; }
        }
        @media (min-width: 861px) {
          .login-mobile-logo { display: none !important; }
        }
      `}</style>
    </div>
  );
}

const FONT = "'Plus Jakarta Sans', system-ui, sans-serif";
const s = {
  root: { minHeight: '100vh', display: 'flex', background: 'var(--bg)', fontFamily: FONT, color: 'var(--text)' },
  brandPanel: {
    flex: '0 0 44%', maxWidth: 620, padding: '44px 48px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
    background: 'var(--accent-gradient)', color: '#fff', position: 'relative', overflow: 'hidden',
  },
  brandTop: { display: 'flex', alignItems: 'center', gap: 14 },
  logoBox: {
    width: 48, height: 48, borderRadius: 14, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, letterSpacing: 1.5, color: '#fff',
  },
  brandName: { fontSize: 17, fontWeight: 800 },
  brandSub: { fontSize: 13, opacity: 0.85 },
  pitch: { fontSize: 34, lineHeight: 1.15, fontWeight: 800, letterSpacing: -0.6, margin: '0 0 24px', maxWidth: 420 },
  features: { listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14, padding: 0 },
  feature: { display: 'flex', alignItems: 'center', gap: 12, fontSize: 14.5, opacity: 0.95, maxWidth: 440 },
  brandFoot: { fontSize: 12.5, opacity: 0.8 },

  formPanel: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative' },
  toggleWrap: { position: 'absolute', top: 18, right: 18 },
  toggle: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' },
  card: { width: '100%', maxWidth: 400 },
  mobileLogo: { display: 'flex', marginBottom: 18 },
  title: { fontSize: 28, fontWeight: 800, letterSpacing: -0.5, margin: '0 0 6px' },
  subtitle: { fontSize: 14.5, color: 'var(--text-2)', margin: '0 0 28px' },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 7 },
  label: { fontSize: 13, fontWeight: 700, color: 'var(--text)' },
  inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  inputIcon: { position: 'absolute', left: 14, color: 'var(--text-3)', pointerEvents: 'none' },
  input: {
    width: '100%', background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 12,
    padding: '12px 42px 12px 42px', color: 'var(--text)', fontSize: 14.5, fontFamily: FONT, outline: 'none',
  },
  eyeBtn: { position: 'absolute', right: 8, background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 6, display: 'flex', borderRadius: 8 },
  errorBox: { display: 'flex', alignItems: 'center', gap: 8, background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 10, padding: '10px 12px', color: 'var(--danger)', fontSize: 13.5 },
  btn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 6,
    background: 'var(--accent)', border: 'none', borderRadius: 12, padding: '13px 16px', color: 'var(--on-accent)',
    fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: FONT, boxShadow: 'var(--shadow-sm)',
  },
  help: { fontSize: 12.5, color: 'var(--text-3)', marginTop: 18 },
};
